import { Check, Eye, X } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import {
  DropdownItem,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
} from "../components/ui/dropdown";
import { cn } from "../lib/utils";

/**
 * "Sehschwächen-Vorschau": the canvas as people with colour vision
 * deficiencies or blurred vision see it. Only the editor view is filtered,
 * never exports. Colour matrices after Machado et al. (2009, severity 1),
 * applied in linear RGB (the SVG filter default).
 */

export const VISION_MODES = [
  "none",
  "protanopia",
  "deuteranopia",
  "tritanopia",
  "grayscale",
  "blur",
] as const;
export type VisionMode = (typeof VISION_MODES)[number];

export const VISION_LABELS: Record<VisionMode, string> = {
  none: "Normal",
  protanopia: "Rotschwäche (Protanopie)",
  deuteranopia: "Grünschwäche (Deuteranopie)",
  tritanopia: "Blauschwäche (Tritanopie)",
  grayscale: "Graustufen",
  blur: "Unscharf (Sehschwäche)",
};

const MATRICES: Partial<Record<VisionMode, number[]>> = {
  protanopia: [
    0.152286, 1.052583, -0.204868, 0.114503, 0.786281, 0.099216, -0.003882, -0.048116, 1.051998,
  ],
  deuteranopia: [
    0.367322, 0.860646, -0.227968, 0.280085, 0.672501, 0.047413, -0.01182, 0.04294, 0.968881,
  ],
  tritanopia: [
    1.255528, -0.076749, -0.178779, -0.078411, 0.930809, 0.147602, 0.004733, 0.691367, 0.3039,
  ],
};

function feMatrix(m: number[]): string {
  const row = (i: number) => `${m[i * 3]} ${m[i * 3 + 1]} ${m[i * 3 + 2]} 0 0`;
  return `${row(0)} ${row(1)} ${row(2)} 0 0 0 1 0`;
}

let mode: VisionMode = "none";
const listeners = new Set<() => void>();

export function setVisionMode(next: VisionMode) {
  mode = next;
  for (const listener of listeners) listener();
}

export function useVisionMode(): VisionMode {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => mode,
  );
}

/**
 * CSS filter for the page element. `blurPx` is in document pixels (the page
 * is scaled with the zoom, so the blur scales with it).
 */
export function visionFilter(current: VisionMode, blurPx: number): string | undefined {
  if (current === "none") return undefined;
  if (current === "grayscale") return "grayscale(1)";
  if (current === "blur") return `blur(${blurPx}px)`;
  return `url(#cva-vision-${current})`;
}

/** SVG filter definitions and the "Vorschau aktiv" badge; rendered inside the canvas viewport. */
export function VisionOverlay() {
  const current = useVisionMode();
  useEffect(() => {
    if (current === "none") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setVisionMode("none");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current]);
  return (
    <>
      <svg width="0" height="0" aria-hidden="true" style={{ position: "absolute" }}>
        <defs>
          {Object.entries(MATRICES).map(([key, matrix]) => (
            <filter key={key} id={`cva-vision-${key}`}>
              <feColorMatrix type="matrix" values={feMatrix(matrix)} />
            </filter>
          ))}
        </defs>
      </svg>
      {current !== "none" ? (
        <div className="pointer-events-none absolute top-3 left-1/2 z-20 -translate-x-1/2">
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setVisionMode("none")}
            title="Vorschau beenden (Esc)"
            className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-zinc-900/85 px-3 py-1.5 text-xs font-medium text-white shadow-lg hover:bg-zinc-900"
          >
            <Eye className="size-3.5" />
            Vorschau: {VISION_LABELS[current]}
            <X className="size-3.5 opacity-80" />
          </button>
        </div>
      ) : null}
    </>
  );
}

/** Submenu of „Ansicht“ in the editor's top bar. */
export function VisionSubmenu() {
  const current = useVisionMode();
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <Eye /> Sehschwächen-Vorschau
        {current !== "none" ? (
          <span className="ml-auto text-xs text-brand-700">{VISION_LABELS[current]}</span>
        ) : null}
      </DropdownSubTrigger>
      <DropdownSubContent className="min-w-64">
        {VISION_MODES.map((entry) => (
          <DropdownItem key={entry} onSelect={() => setVisionMode(entry)}>
            <Check className={cn("size-4", entry === current ? "opacity-100" : "opacity-0")} />
            {VISION_LABELS[entry]}
          </DropdownItem>
        ))}
        <p className="max-w-64 px-2 pt-1 pb-1.5 text-[11px] text-zinc-500">
          Nur die Ansicht im Editor – Exporte bleiben unverändert. Esc beendet die Vorschau.
        </p>
      </DropdownSubContent>
    </DropdownSub>
  );
}
